import { Injectable } from '@angular/core';
import { Order, QueryEntity } from '@datorama/akita';
import {
	compareEntities,
	compareEntity,
	isEquals,
} from '@lycoris-nubila/ngx-lycoris';
import { first, isEmpty, isNil, reject, uniqBy } from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { ModuleSort } from 'module/model/module.sort';
import { ModuleType } from 'module/model/module.type';
import { Module } from 'module/module';
import { ModuleState, ModuleStore } from 'module/module.store';
import { ModuleRepositoryPort } from 'module/port/module-repository.port';
import { ObserveModulesByIdsQuery } from 'module/query/observe-modules-by-ids.query';
import { ObserveModulesQuery } from 'module/query/observe-modules.query';

import { CacheObservable } from '../../app-decorators';

@Injectable()
export class ModuleRepositoryAdapter
	extends QueryEntity<ModuleState>
	implements ModuleRepositoryPort
{
	constructor(protected store: ModuleStore) {
		super(store);
	}

	@CacheObservable()
	public observeModules(query: ObserveModulesQuery): Observable<Module[]> {
		return this.selectAll({
			filterBy: (entity) =>
				(!query.moduleType || entity.type === query.moduleType) &&
				(!query.name ||
					entity.localizations.some((loc) =>
						new RegExp(query.name, 'gmi').test(loc.name)
					)) &&
				(isEmpty(query.phasesId) || query.phasesId.includes(entity.phaseId)) &&
				(!query.organizationId ||
					isEquals(query.organizationId, entity.organizationId)),
			sortBy: (a, b) => {
				switch (query.sort) {
					case ModuleSort.NAME:
						return (
							(
								a.localizations.find(
									(localization) => localization.locale === query.sortNameByLocale
								) ?? first(a.localizations)
							)?.name.localeCompare(
								(
									b.localizations.find(
										(localization) => localization.locale === query.sortNameByLocale
									) ?? first(b.localizations)
								)?.name
							) || 0
						);
					default:
						return a.position - b.position;
				}
			},
		}).pipe(
			map((documents) =>
				documents.map((document) => this.store.asEntity(document))
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeModuleById(moduleId: string): Observable<Module> {
		return this.selectAll({
			filterBy: (entity) =>
				isEquals(entity.id, moduleId) || isEquals(entity.moduleId, moduleId),
			limitTo: 1,
		}).pipe(
			map((documents) => first(documents)),
			map((document) => this.store.asEntity(document)),
			distinctUntilChanged(compareEntity),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeModuleByType(type: ModuleType): Observable<Module> {
		return this.selectAll({
			filterBy: (entity) => isEquals(entity.type, type),
		}).pipe(
			map((documents) =>
				documents.map((document) => this.store.asEntity(document))
			),
			map((modules) => first(modules)),
			distinctUntilChanged(compareEntity),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeModulesByIds(
		query: ObserveModulesByIdsQuery
	): Observable<Module[]> {
		return this.selectAll({
			filterBy: (entity) =>
				query.ids.includes(entity.id) || query.ids.includes(entity.moduleId),
			sortByOrder: Order.DESC,
			sortBy: (a, b) => {
				if (query.sort) {
					switch (query.sort) {
						case ModuleSort.NAME:
							return (
								(
									a.localizations.find(
										(localization) => localization.locale === query.sortNameByLocale
									) ?? first(a.localizations)
								)?.name
									.toLowerCase()
									.localeCompare(
										(
											b.localizations.find(
												(localization) => localization.locale === query.sortNameByLocale
											) ?? first(b.localizations)
										)?.name.toLowerCase()
									) || 0
							);
						default:
							return a.position - b.position;
					}
				}

				return a.position - b.position;
			},
		}).pipe(
			map((documents) =>
				documents.map((document) => this.store.asEntity(document))
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public async saveModules(entities: Module[]): Promise<void> {
		this.store.upsertMany(
			uniqBy(
				reject(entities, isNil).map((e) => this.store.asDocument(e)),
				'id'
			)
		);
	}
}
